import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import type { AppDispatch } from "../../../store";
import { fetchAllCustomerSites, stopPollingCustomerSites } from "../../../redux/services/customerService";
import {
    clearUserInfo,
    clearWeatherInfo,
    setSelectedCustomerName,
    setSelectedSite,
    setSelectedTimezone
} from "../../../redux/slices/customerSlice";
import { clearPowerState } from "../../../redux/slices/powerSlice";
import { clearChargerData } from "../../../redux/slices/chargerSlice";
import { clearDashboardData, clearPowerFuelData } from "../../../redux/slices/dashboardSlice";
import { isEmpty } from "lodash";
import { FactoryPathUrl, ExecutiveDashboardUrl } from "../../../utils/common";
import { DASHBOARD, GENERAL } from "../../../utils/constants";

export const useCustomerSiteManager = (
    selectedCustomerName: string,
    permittedCustomers: any[],
    customerSites: any[],
    selectedSite: string,
    FactoryPath: boolean,
    ExecutivePath: boolean
) => {
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const { customer, site }: any = useParams();
    const notFactoryAndExecutiveDashboard = !FactoryPathUrl(FactoryPath) && !ExecutiveDashboardUrl(ExecutivePath);

    // Handle customer site fetching and navigation
    useEffect(() => {
        if (permittedCustomers.length > 0 && selectedCustomerName !== customer && notFactoryAndExecutiveDashboard && isEmpty(customer)) {
            const isValidCustomer = permittedCustomers.some((customer) => customer.customer === selectedCustomerName);
            if (isValidCustomer) {
                dispatch(fetchAllCustomerSites(selectedCustomerName)).then((response: any) => {
                    if (isEmpty(site)) {
                        const sites = response?.payload || [];
                        if (sites.length === 1) {
                            dispatch(setSelectedSite(sites[0].name));
                            navigate(`/${selectedCustomerName}/${sites[0].name}/${GENERAL}`);
                        } else if (notFactoryAndExecutiveDashboard) {
                            dispatch(setSelectedSite(""));
                            navigate(`/${selectedCustomerName}/${DASHBOARD}`);
                        }
                    }
                });
            }
        } else if (selectedCustomerName === customer && permittedCustomers.length > 0 && customerSites.length === 0 && site && notFactoryAndExecutiveDashboard) {
            const isValidCustomer = permittedCustomers.some((customer) => customer.customer === selectedCustomerName);
            if (isValidCustomer) {
                dispatch(fetchAllCustomerSites(selectedCustomerName)).then((response: any) => {
                    if (response?.payload === "Request canceled") {
                        return;
                    } else {
                        const sites = response?.payload || [];
                        if (sites.find((x: any) => x.name.toLowerCase() === site.toLowerCase())) {
                            dispatch(setSelectedSite(site));
                        } else {
                            navigate("/404");
                        }
                    }
                });
            }
        } else if (selectedCustomerName === customer && permittedCustomers.length > 0 && customerSites.length === 0 && isEmpty(site) && notFactoryAndExecutiveDashboard) {
            const isValidCustomer = permittedCustomers.some((customer) => customer.customer === selectedCustomerName);
            if (isValidCustomer) {
                dispatch(fetchAllCustomerSites(selectedCustomerName)).then((response: any) => {
                    const sites = response?.payload || [];
                    if (sites.length === 1) {
                        dispatch(setSelectedSite(sites[0].name));
                        navigate(`/${selectedCustomerName}/${sites[0].name}/${GENERAL}`);
                    } else if (customer !== selectedCustomerName) {
                        navigate(`/${selectedCustomerName}/${DASHBOARD}`);
                    }
                });
            }
        }
    }, [selectedCustomerName, permittedCustomers, FactoryPathUrl(FactoryPath), ExecutiveDashboardUrl(ExecutivePath)]);

    // Handle customer selection based on URL params
    useEffect(() => {
        if (customer && !isEmpty(permittedCustomers) && isEmpty(site) && notFactoryAndExecutiveDashboard && selectedCustomerName !== customer) {
            if (permittedCustomers.find((data) => data.customer === customer)) {
                dispatch(setSelectedCustomerName(customer));
                dispatch(setSelectedTimezone(permittedCustomers.find((data) => data.customer === customer)?.preferences?.timezone || "US/Pacific"));
            } else {
                dispatch(setSelectedCustomerName(permittedCustomers[0]?.customer));
                dispatch(setSelectedTimezone(permittedCustomers[0]?.preferences?.timezone || "US/Pacific"));
                navigate("/404");
            }
        } else if (!isEmpty(customer) && !isEmpty(site) && !isEmpty(permittedCustomers) && notFactoryAndExecutiveDashboard) {
            if (permittedCustomers.find((data) => data.customer === customer)) {
                dispatch(setSelectedCustomerName(customer));
                dispatch(setSelectedTimezone(permittedCustomers.find((data) => data.customer === customer)?.preferences?.timezone || "US/Pacific"));
            }
        } else if (isEmpty(customer) && isEmpty(site) && !isEmpty(permittedCustomers) && notFactoryAndExecutiveDashboard) {
            dispatch(setSelectedCustomerName(permittedCustomers[0]?.customer));
            dispatch(setSelectedTimezone(permittedCustomers[0]?.preferences?.timezone || "US/Pacific"));
        }
    }, [customer, permittedCustomers]);

    // Handle state clearing when customer/site changes
    useEffect(() => {
        if (!isEmpty(customer) && !isEmpty(site) && customer !== selectedCustomerName && site !== selectedSite && !isEmpty(selectedCustomerName) && !isEmpty(selectedSite) && notFactoryAndExecutiveDashboard) {
            if (customer !== selectedCustomerName || selectedCustomerName === "") {
                dispatch(clearUserInfo());
                dispatch(clearWeatherInfo());
                dispatch(clearPowerState());
                dispatch(clearPowerFuelData());
                dispatch(clearChargerData());
                const selectedCustomerObj = permittedCustomers.find((data) => data.customer === customer);
                if (selectedCustomerObj) {
                    dispatch(setSelectedCustomerName(customer));
                    dispatch(setSelectedTimezone(permittedCustomers.find((data) => data.customer === customer)?.preferences?.timezone || "US/Pacific"));
                }
            } else if (site !== selectedSite) {
                const siteExists = customerSites.find((siteData: any) => siteData.name === site);
                if (siteExists) {
                    dispatch(setSelectedSite(site));
                }
            }
        } else if ((isEmpty(site) && !isEmpty(customer) && customer !== selectedCustomerName) || (!isEmpty(customer) && customer !== selectedCustomerName) || (!isEmpty(customer) && customer !== selectedCustomerName && isEmpty(selectedSite) && notFactoryAndExecutiveDashboard)) {
            dispatch(clearUserInfo());
            dispatch(clearPowerState());
            dispatch(clearPowerFuelData());
            dispatch(clearChargerData());
            dispatch(clearDashboardData());
            if (!isEmpty(customer) && !isEmpty(site) && permittedCustomers?.length > 0) {
                const selectedCustomerObj = permittedCustomers.find((data) => data.customer === customer);
                if (selectedCustomerObj) {
                    dispatch(setSelectedCustomerName(customer));
                    dispatch(setSelectedTimezone(permittedCustomers.find((data) => data.customer === customer)?.preferences?.timezone || "US/Pacific"));
                }
            }
        }
    }, [customer, site]);

    // Cleanup polling on unmount to prevent memory leaks
    useEffect(() => {
        return () => {
            stopPollingCustomerSites();
        };
    }, []);
};